
// apps/admin-ui/src/components/Layout.tsx
// Admin Layout with sidebar navigation matching CSR style
// ALL RIGHTS RESERVED
//
// UPDATED 2026-02-02: Added DEX On-Ramp sidebar link
// UPDATED 2026-02-05: Added Ledger section with Block Explorer link
// UPDATED 2026-02-07: Phase 1 RBAC — sidebar links gated by permission

import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCurrentEmployee, logout } from '../utils/auth';
import { hasPermission, type Permission } from '../utils/rbac';
import { UserSettings } from './UserSettings';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const employee = getCurrentEmployee();

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  // Permission check helper scoped to current employee
  const canSee = (permission: Permission): boolean => {
    if (!employee) return false;
    return hasPermission(employee.roles, permission);
  };

  // Pre-compute visibility for each sidebar link
  const showOrders       = canSee('dashboard:view_transactions');
  const showDexOnRamp    = canSee('dashboard:view_mint_requests');
  const showBlockExplorer = canSee('dashboard:view_anchors');
  const showContracts    = canSee('contracts:view');
  const showCompliance   = canSee('compliance:kyc_aml_review');
  const showTreasury     = canSee('reserve:view_balances');
  const showGovernance   = canSee('governance:view_proposals');

  // Show sections only if they have at least one visible child
  const showExchange     = showOrders || showDexOnRamp;
  const showLedger       = showBlockExplorer;
  const showOperations   = showContracts || showCompliance || showTreasury;
  const showGovernanceSec = showGovernance;

  return (
    <div className="min-h-screen bg-surface text-content">
      {/* Header */}
      <header className="h-14 border-b border-edge bg-surface-subtle flex items-center justify-between px-4">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8">
              <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
                <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="2" className="text-logo" />
                <path d="M32 14 L46 22.5 L46 41.5 L32 50 L18 41.5 L18 22.5 Z" stroke="currentColor" strokeWidth="2" className="text-logo" />
                <circle cx="32" cy="32" r="8" fill="currentColor" className="text-logo" />
              </svg>
            </div>
            <span className="font-semibold text-content">CXXR <span className="text-content-secondary font-normal">Admin</span></span>
          </Link>
        </div>

        <div className="flex items-center gap-4">
          {employee && (
            <UserSettings
              employeeName={employee.name}
              employeeRoles={employee.roles.join(', ')}
              onLogout={handleLogout}
            />
          )}
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 border-r border-edge bg-surface-subtle min-h-[calc(100vh-3.5rem)]">
          <nav className="p-3 space-y-1">
            <SidebarSection title="Overview">
              <SidebarLink 
                to="/" 
                active={location.pathname === '/'}
              >
                Dashboard
              </SidebarLink>
            </SidebarSection>

            {showExchange && (
              <SidebarSection title="Exchange">
                {showOrders && (
                  <SidebarLink 
                    to="/orders" 
                    active={location.pathname === '/orders'}
                  >
                    Exchange Orders
                  </SidebarLink>
                )}
                {showDexOnRamp && (
                  <SidebarLink 
                    to="/admin/dex/onramps" 
                    active={location.pathname === '/admin/dex/onramps'}
                  >
                    DEX On-Ramp
                  </SidebarLink>
                )}
              </SidebarSection>
            )}

            {showLedger && (
              <SidebarSection title="Ledger">
                <SidebarLink 
                  to="/admin/anchor/blocks" 
                  active={location.pathname === '/admin/anchor/blocks'}
                >
                  Block Explorer
                </SidebarLink>
              </SidebarSection>
            )}

            {showOperations && (
              <SidebarSection title="Operations">
                {showContracts && (
                  <SidebarLink 
                    to="/contracts" 
                    active={location.pathname === '/contracts'}
                  >
                    Smart Contracts
                  </SidebarLink>
                )}
                {showCompliance && (
                  <SidebarLink 
                    to="/compliance" 
                    active={location.pathname === '/compliance'}
                  >
                    Compliance
                  </SidebarLink>
                )}
                {showTreasury && (
                  <SidebarLink 
                    to="/treasury" 
                    active={location.pathname === '/treasury'}
                  >
                    Treasury
                  </SidebarLink>
                )}
              </SidebarSection>
            )}

            {showGovernanceSec && (
              <SidebarSection title="Governance">
                <SidebarLink 
                  to="/admin/governance" 
                  active={location.pathname === '/admin/governance'}
                >
                  Council Voting
                </SidebarLink>
              </SidebarSection>
            )}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-h-[calc(100vh-3.5rem)]">
          {children}
        </main>
      </div>
    </div>
  );
}

function SidebarSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="pt-3 first:pt-0">
      <div className="px-2 mb-1.5 text-2xs font-medium text-content-muted uppercase tracking-wider">
        {title}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function SidebarLink({ 
  to, 
  active, 
  count, 
  children 
}: { 
  to: string; 
  active: boolean; 
  count?: number; 
  children: ReactNode 
}) {
  return (
    <Link
      to={to}
      className={`
        flex items-center justify-between px-2 py-1.5 rounded text-sm
        transition-colors
        ${active 
          ? 'bg-brand-500/10 text-brand-500' 
          : 'text-content-secondary hover:text-content hover:bg-surface-emphasis'
        }
      `}
    >
      <span>{children}</span>
      {count !== undefined && count > 0 && (
        <span className={`
          text-2xs px-1.5 py-0.5 rounded
          ${active 
            ? 'bg-brand-500/20 text-brand-500' 
            : 'bg-surface-emphasis text-content-muted'
          }
        `}>
          {count}
        </span>
      )}
    </Link>
  );
}

export default Layout;
